import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import FadeIn from "@/components/FadeIn";

const FAQS = [
  {
    q: "Quel montant d'aides puis-je obtenir ?",
    a: "Le cumul dépend de vos revenus et de votre projet. Pour une pompe à chaleur air/eau : MaPrimeRénov' jusqu'à 5 000 € (5 000 € très modestes, 4 000 € modestes, 3 000 € intermédiaires) et une prime CEE jusqu'à environ 5 800 €, soit jusqu'à 10 800 € d'aides cumulées. À cela s'ajoutent l'éco-PTZ jusqu'à 15 000 € et la TVA réduite à 5,5 %.",
  },
  {
    q: "Puis-je cumuler MaPrimeRénov', la prime CEE et l'éco-PTZ ?",
    a: "Oui. Ces aides sont cumulables entre elles : MaPrimeRénov' et la prime CEE réduisent directement votre coût, tandis que l'éco-PTZ finance sans intérêts le reste à charge, remboursable jusqu'à 20 ans.",
  },
  {
    q: "Faut-il déposer le dossier d'aides avant de signer le devis ?",
    a: "Oui, l'ordre des démarches est impératif : MaPrimeRénov' comme la prime CEE se demandent avant la signature du devis, sous peine de perdre l'aide. Nous montons et déposons votre dossier à votre place, au bon moment.",
  },
  {
    q: "Ma pompe à chaleur fonctionnera-t-elle par grand froid ?",
    a: "Oui. Les pompes à chaleur air/eau récentes chauffent votre logement et votre eau chaude jusqu'à environ -15 °C, voire -20 °C selon les modèles. Un dimensionnement adapté à votre logement est fait lors de la visite technique.",
  },
  {
    q: "Dois-je changer mes radiateurs ?",
    a: "Pas forcément. Un plancher chauffant ou des radiateurs basse température sont idéaux. Avec des radiateurs haute température, nous étudions leur compatibilité et, si besoin, proposons un remplacement ciblé pour garder les performances de la PAC.",
  },
  {
    q: "Combien de temps durent les travaux ?",
    a: "Pour une installation standard, comptez généralement 2 à 3 jours : dépose de l'ancienne chaudière, pose de l'unité extérieure, raccordement au circuit de chauffage et mise en service avec explications complètes.",
  },
  {
    q: "Dans quelles zones intervenez-vous ?",
    a: "Nous intervenons en Gironde, Charente, Charente-Maritime, Dordogne, Lot-et-Garonne et Landes depuis notre bureau de Saint-André-de-Cubzac (33240).",
  },
];

export default function Faq() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="faq"
      data-testid="section-faq"
      className="bg-paper py-24 text-ink sm:py-32"
    >
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <FadeIn>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-700 sm:text-xs">
            FAQ
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-5xl">
            Les questions qu'on nous pose le plus.
          </h2>
        </FadeIn>

        <div className="mt-12 divide-y divide-slate-200 border-y border-slate-200">
          {FAQS.map((f, i) => {
            const open = openIdx === i;
            return (
              <FadeIn key={i} delay={i * 0.04}>
                <div data-testid={`faq-item-${i}`}>
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span className="font-display text-base font-bold sm:text-lg">
                      {f.q}
                    </span>
                    <Plus
                      className={`h-5 w-5 shrink-0 text-emerald-700 transition-transform duration-300 ${
                        open ? "rotate-45" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-10 text-sm leading-relaxed text-slate-600">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}
